// WP1B PROJ-UPD: render a `confirm_card` block — a short conversational
// confirmation with a few named action buttons. Unlike FormBlockView,
// there are no input fields: a click IS the submission. Confirm cards
// NEVER call an API directly — same rule as forms — the submission is a
// normal user turn back into the chat stream, and the assistant decides
// what to do (call an MCP tool, ask a follow-up, or just acknowledge).

import { useChatConversationContext } from '@/components/chat/useChatConversation'
import { useId, useState } from 'react'
import { t } from '@/lib/i18n'

interface ConfirmCardAction {
  value: string
  label: string
}

interface Props { //@usedby:ComposedBody.tsx
  id: string
  text: string
  actions: ConfirmCardAction[]
  payload?: Record<string, unknown>
}

export function ConfirmCardBlockView({ id, text, actions, payload }: Props) {
  const { sendMessage } = useChatConversationContext()
  const [selected, setSelected] = useState<string | null>(null)
  const groupId = useId()

  const handleAction = (action: ConfirmCardAction) => {
    if (selected) return
    setSelected(action.value)
    // WP1B: the model needs the full [CONFIRM_SUBMIT] envelope, but the
    // click itself must not produce a visible chat bubble at all — the
    // card's own in-place "Selected — the assistant will respond
    // shortly." status (below) is the only user-visible feedback. Pass
    // an empty displayText; useChatConversation treats an empty user
    // turn as hidden, so no bubble ever renders for it.
    void sendMessage(
      buildConfirmSubmitMessage(id, action.value, action.label, payload ?? {}),
      '',
    )
  }

  return (
    <div
      className="rounded-xl border border-border bg-card p-4 space-y-3"
      aria-labelledby={`${groupId}-text`}
    >
      <div id={`${groupId}-text`} className="text-sm text-foreground">
        {text}
      </div>
      <div className="flex items-center justify-end gap-2 pt-1">
        {selected ? (
          <span className="text-xs text-foreground-muted">
            {t('Selected — the assistant will respond shortly.')}
          </span>
        ) : (
          actions.map((action, i) => (
            <button
              key={action.value}
              type="button"
              onClick={() => handleAction(action)}
              disabled={selected !== null}
              className={
                i === 0
                  ? 'bg-primary hover:bg-primary-hover text-primary-foreground rounded-lg px-3 py-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                  : 'bg-transparent hover:bg-card border border-input text-foreground rounded-lg px-3 py-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              }
            >
              {action.label}
            </button>
          ))
        )}
      </div>
    </div>
  )
}

// Exported for unit tests; the chatbot's system prompt teaches the LLM
// to recognise this exact prefix (server/chat_views.py confirm_card doc).
export function buildConfirmSubmitMessage( //@usedby:ConfirmCardBlockView.test.tsx
  cardId: string,
  action: string,
  label: string,
  payload: Record<string, unknown>,
): string {
  const body = JSON.stringify({ card_id: cardId, action, payload })
  return [
    `[CONFIRM_SUBMIT id="${cardId}"]`,
    body,
    '[/CONFIRM_SUBMIT]',
    '',
    `(Selected "${label}" on confirmation card "${cardId}".)`,
  ].join('\n')
}
